import { useEffect, useState, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Search,
  RotateCw,
  Play,
  Plus,
  AlertTriangle,
} from 'lucide-react';
import {
  adminApi,
  AdminOrganization,
} from '../services/adminApi';
import { getProductName } from './productCatalog';
import { formatDate } from './formatDate';
import { getOrgDetailUrl } from './adminNavigation';
import { CreateOrganizationDialog } from './CreateOrganizationDialog';

const PAGE_SIZE = 50;

export const formatTokens = (value: number | null | undefined): string => {
  if (value == null) return '-';
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return String(value);
};

function OrganizationsTab() {
  const [organizations, setOrganizations] = useState<AdminOrganization[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [createOpen, setCreateOpen] = useState(false);

  const fetchOrganizations = useCallback(async (skipArg: number) => {
    try {
      setLoading(true);
      const response = await adminApi.listOrganizations(
        statusFilter || undefined,
        debouncedSearchTerm || undefined,
        skipArg,
        PAGE_SIZE
      );
      setOrganizations(response.organizations);
      setTotalCount(response.totalCount);
      setError(null);
    } catch (err: unknown) {
      let errorMsg = 'Failed to fetch organizations';
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosErr = err as { response?: { data?: { error?: string; details?: string }; status?: number } };
        if (axiosErr.response?.data?.details) {
          errorMsg = `${axiosErr.response.data.error}: ${axiosErr.response.data.details}`;
        } else if (axiosErr.response?.data?.error) {
          errorMsg = axiosErr.response.data.error;
        }
      } else if (err instanceof Error) {
        errorMsg = err.message;
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, debouncedSearchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to the first page whenever the status filter or search term changes,
  // and fetch that first page. skip is the single source of truth for paging;
  // there is intentionally no separate effect keyed on skip (Prev/Next drive it directly).
  useEffect(() => {
    setSkip(0);
    fetchOrganizations(0);
  }, [statusFilter, debouncedSearchTerm]);

  const handleRefresh = () => {
    fetchOrganizations(skip);
  };

  const handlePrevious = () => {
    const next = Math.max(0, skip - PAGE_SIZE);
    setSkip(next);
    fetchOrganizations(next);
  };

  const handleNext = () => {
    const next = skip + PAGE_SIZE;
    setSkip(next);
    fetchOrganizations(next);
  };

  const handleViewDetails = (org: AdminOrganization) => {
    window.location.href = getOrgDetailUrl(org.id);
  };

  const handleReactivate = async (org: AdminOrganization) => {

    try {
      await adminApi.reactivateOrganization(org.id);
      handleRefresh();
    } catch (err) {
      console.error('Failed to reactivate organization:', err);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Created column keeps the locale-default short date (e.g. "7/16/2026").
  // Trial-badge dates use the shared formatDate() ("Jul 16, 2026") for parity with OrgBillingTab.
  const formatCreatedAt = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  // Status color semantics preserved from the Fluent Badge mapping:
  // Active = success/emerald, Trial = warning/amber, Suspended = destructive/red,
  // Deleted = neutral/gray. Hues mirror aieweb's ProductSubscriptionRow chips.
  const STATUS_BADGE_CLASS: Record<string, string> = {
    Active: 'border-transparent bg-gold/15 text-foreground',
    Trial: 'border-transparent bg-warning/10 text-warning',
    Suspended: 'border-transparent bg-destructive/10 text-destructive',
    Deleted: 'border-transparent bg-muted text-text-secondary',
  };

  const getStatusBadge = (status: string) => (
    <Badge variant="outline" className={STATUS_BADGE_CLASS[status] || STATUS_BADGE_CLASS.Deleted}>
      {status}
    </Badge>
  );

  const returnedCount = organizations.length;
  const rangeStart = totalCount === 0 ? 0 : skip + 1;
  const rangeEnd = skip + returnedCount;
  const isFirstPage = skip === 0;
  const isLastPage = skip + returnedCount >= totalCount;

  if (loading && organizations.length === 0) {
    return (
      <div className="admin-loading">
        <Spinner size={32} label="Loading organizations..." />
      </div>
    );
  }

  return (
    <div className="organizations-tab">
      {error && (
        <div className="admin-error">
          <AlertTriangle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Organizations Table */}
      <div className="subscriptions-section">
        <div className="section-header">
          <h2>Organizations</h2>
          <div className="action-buttons">
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus />
              Create organization
            </Button>
            <Button variant="ghost" size="sm" onClick={handleRefresh}>
              <RotateCw />
              Refresh
            </Button>
          </div>
        </div>

        <div className="filters-row">
          <div className="search-input relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by name, ID, or billing email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select
            value={statusFilter || 'all'}
            onValueChange={(value) => setStatusFilter(value === 'all' ? '' : value)}
          >
            <SelectTrigger aria-label="Filter by status" className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Trial">Trial</SelectItem>
              <SelectItem value="Suspended">Suspended</SelectItem>
              <SelectItem value="Deleted">Deleted</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Focusable so keyboard users can scroll the table when it is wider than the screen. */}
        <div className="org-list-scroll" tabIndex={0} role="region" aria-label="Organizations table">
        <Table className="org-list-table">
          <colgroup>
            <col style={{ width: '21.5%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '4.5%' }} />
            <col style={{ width: '6.5%' }} />
            <col style={{ width: '6.5%' }} />
            <col style={{ width: '6.5%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '6.5%' }} />
          </colgroup>
          <TableHeader>
            <TableRow>
              <TableHead>Organization</TableHead>
              <TableHead>ID</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>AIEs</TableHead>
              <TableHead>Token Balance</TableHead>
              <TableHead>Tokens Used</TableHead>
              <TableHead>Tokens Granted</TableHead>
              <TableHead>Spend</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {organizations.map(org => (
              <TableRow
                key={org.id}
                className="clickable-row"
                onClick={() => handleViewDetails(org)}
                style={{ cursor: 'pointer' }}
              >
                <TableCell title={org.displayName || org.name}>{org.displayName || org.name}</TableCell>
                <TableCell title={org.id}><code>{org.id}</code></TableCell>
                <TableCell>
                  {getStatusBadge(org.status)}
                  {org.productTrials && org.productTrials.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '4px', alignItems: 'flex-start' }}>
                      {org.productTrials.map((trial) => (
                        <Badge
                          key={trial.productId}
                          variant="outline"
                          className="border-warning/30 text-warning px-2 py-0 text-[10px] font-medium"
                        >
                          {getProductName(trial.productId)} Trial
                          {trial.trialEndsAt ? ` · ends ${formatDate(trial.trialEndsAt)}` : ''}
                        </Badge>
                      ))}
                    </div>
                  )}
                </TableCell>
                <TableCell>{org.subscribedEmployeeCount}</TableCell>
                <TableCell>{formatTokens(org.tokenBalance)}</TableCell>
                <TableCell>{formatTokens(org.totalTokensConsumed)}</TableCell>
                <TableCell>{formatTokens(org.totalTokensGranted)}</TableCell>
                <TableCell>{formatCurrency(org.currentMonthSpend)}</TableCell>
                <TableCell>{formatCreatedAt(org.createdAt)}</TableCell>
                <TableCell onClick={e => e.stopPropagation()}>
                  <div className="action-buttons">
                    {org.status === 'Suspended' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        title="Reactivate"
                        onClick={() => handleReactivate(org)}
                      >
                        <Play />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>

        <div className="pagination">
          <Button
            onClick={handlePrevious}
            disabled={isFirstPage || loading}
            variant="secondary"
          >
            Previous
          </Button>
          <span>
            Showing {rangeStart}–{rangeEnd} of {totalCount} organizations
          </span>
          <Button
            onClick={handleNext}
            disabled={isLastPage || loading}
            variant="secondary"
          >
            Next
          </Button>
        </div>
      </div>
      {createOpen && <CreateOrganizationDialog onClose={() => setCreateOpen(false)} />}
    </div>
  );
}

export default OrganizationsTab;
